// =============================================================
// pdfPages.js — turn PDF pages into JPEG images IN THE BROWSER.
//
// Why here and not on the API: the image model takes images only, and
// rasterizing a PDF needs a native binding the Functions runtime cannot load.
// The browser already has a canvas, so pdf.js runs here.
//
// pdf.js is loaded on demand from the CDN the CSP already allows for scripts
// (cdn.jsdelivr.net), so it costs nothing on any page that never draws a
// rendering. The worker is fetched as text and started from a blob URL,
// because worker-src allows 'self' and blob: but not the CDN.
// isEvalSupported is false: pdf.js before 4.2.67 could be made to run script
// from a crafted font with eval on, and a submitted PDF is somebody else's file.
// =============================================================

const PDFJS_VERSION = '4.10.38';
const PDFJS_BASE = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/legacy/build`;

let pdfjsPromise = null;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const lib = await import(/* webpackIgnore: true */ `${PDFJS_BASE}/pdf.min.mjs`);
      const res = await fetch(`${PDFJS_BASE}/pdf.worker.min.mjs`);
      if (!res.ok) throw new Error(`Could not load the PDF reader (${res.status})`);
      const url = URL.createObjectURL(new Blob([await res.text()], { type: 'text/javascript' }));
      lib.GlobalWorkerOptions.workerPort = new Worker(url, { type: 'module' });
      return lib;
    })();
    pdfjsPromise.catch(() => { pdfjsPromise = null; });
  }
  return pdfjsPromise;
}

/** Scale that makes the page's longer side `longSide` pixels. Pure. */
export function scaleFor(width, height, longSide) {
  const long = Math.max(Number(width) || 0, Number(height) || 0);
  return long > 0 ? longSide / long : 1;
}

/**
 * Renders up to `maxPages` pages of the PDF at `url` to JPEG.
 * Returns [{ page, contentType, data }] with `data` as base64 (no data: prefix).
 */
export async function renderPdfPages(url, { maxPages = 20, longSide = 2000, quality = 0.82 } = {}) {
  const lib = await loadPdfjs();
  const pdf = await lib.getDocument({ url, isEvalSupported: false }).promise;
  const out = [];
  try {
    const count = Math.min(pdf.numPages, maxPages);
    for (let n = 1; n <= count; n += 1) {
      const page = await pdf.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: scaleFor(base.width, base.height, longSide) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext('2d');
      // Plan sheets are line drawings on a transparent page; JPEG has no alpha,
      // so paint the page white first or it comes out black.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      out.push({ page: n, contentType: 'image/jpeg', data: dataUrl.slice(dataUrl.indexOf(',') + 1) });
      page.cleanup();
    }
  } finally {
    pdf.destroy();
  }
  return out;
}
